import { Button, Input, message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import html2pdf from "html2pdf.js";
import { Html5Qrcode } from "html5-qrcode";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import noData from "../Assets/no-data-image.svg";
import { CREATE_jwel } from "../Config/Config";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";
import { printReceipt } from "./eposPrint";
import styles from "./BarCodeScanner.module.css";
import { BillPdfDownload } from "./BillPdfDownload";
import CashMethod from "./CashMethod";
import CustomerDialog from "./CustomerDialog";
import CustomerInformation from "./CustomerInformation";
import EditItemDetails from "./EditItemDetails";
import HomeBillDialog from "./HomeBillDialog";
import ModifyInvoiceNo from "./ModifyInvoiceNo";
import OfferDetailsADialog from "./OfferDetailsADialog";
import OfferDetailsBDialog from "./OfferDetailsBDialog";
import QrCodeDialog from "./QrCodeDialog";
import SingleCardDetails from "./SingleCardDetails";
import TaansDetailsDialog from "./TaansDetailsDialog";
import WhatsAppDialog from "./WhatsAppDialog";

/**
 * NOTE FOR THE DEV TEAM
 * ---------------------------------------------------------------------
 * Mobile / tablet version of the Exchange Bill screen.
 *
 *  - SALE | EXCHANGE switch under the invoice hero. The Design Code box,
 *    the scanner, the Home/Taans dialogs and the item list all follow the
 *    active mode (SALE items -> barCodeData, EXCHANGE items -> exchangeData).
 *  - Net Amount = Sale net - Exchange net (same rule as the desktop screen).
 *  - Save/Update: if the bill has exchange items it posts to
 *    /api/ExchangeBill/Save|Update (same payload as desktop), otherwise it
 *    keeps posting to /api/BillGeneration/Save|Update exactly as before.
 *  - Modify: loads TYPE='B' rows into SALE and TYPE='EB' rows into EXCHANGE.
 *  - Print / PDF / WhatsApp: exchange lines are appended as negative rows
 *    ("[EXCH] ...") so the existing eposPrint / BillPdfDownload helpers
 *    work without signature changes.
 *  - Layout: phone = single column; >=700px (tablet) = 2 column layout with a
 *    sticky entry panel; >=900px = item cards in 2 columns.
 * ---------------------------------------------------------------------
 */

/* ------------------------------------------------------------------ */
/*  Lightweight line icons (no extra dependency, inherit currentColor) */
/* ------------------------------------------------------------------ */
const ICONS = {
  edit: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </>
  ),
  userPlus: (
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21a7 7 0 0 1 14 0" />
      <path d="M19 8v6M16 11h6" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  trash: (
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M6 6l1 14h10l1-14" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  receipt: (
    <>
      <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2z" />
      <path d="M9 8h6M9 12h6" />
    </>
  ),
  wallet: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h13v4" />
      <path d="M3 7v11a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1H5a2 2 0 0 1-2-2z" />
      <circle cx="16.5" cy="14.5" r="1" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20h1" />
    </>
  ),
  scan: (
    <>
      <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 12h10" />
    </>
  ),
  save: (
    <>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <path d="M7 3v5h8V3M7 21v-7h10v7" />
    </>
  ),
  printer: (
    <>
      <path d="M6 9V3h12v6" />
      <rect x="3" y="9" width="18" height="8" rx="2" />
      <path d="M7 14h10v7H7z" />
    </>
  ),
  close: <path d="M18 6L6 18M6 6l12 12" />,
  chat: <path d="M21 12a9 9 0 0 1-13.4 7.8L3 21l1.3-4.4A9 9 0 1 1 21 12z" />,
  cart: (
    <>
      <circle cx="9" cy="20" r="1.5" />
      <circle cx="18" cy="20" r="1.5" />
      <path d="M2 3h3l2.5 12h11L21 7H6" />
    </>
  ),
  swap: (
    <>
      <path d="M7 4L3 8l4 4" />
      <path d="M3 8h14" />
      <path d="M17 20l4-4-4-4" />
      <path d="M21 16H7" />
    </>
  ),
};

const Ico = ({ name, size = 16, onClick, className }) => (
  <svg
    className={className}
    onClick={onClick}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICONS[name]}
  </svg>
);

const EMPTY_CUSTOMER_FORM = {
  mobile1: "",
  city: "",
  name: "",
  mobile2: "",
  phone: "",
  address1: "",
  address2: "",
  address3: "",
  address4: "",
  state: "ANDHRA PRADESH",
  stateCode: "37",
  gstin: "",
  panNo: "",
  idProof: "",
  dob: "",
  anniversary: "",
  locationType: "LOCAL",
};

/* Pure totals helper – used for sale items, exchange items and the signed print list */
const calcTotals = (items) => {
  let net = 0,
    gross = 0,
    dis = 0,
    qty = 0,
    basic = 0,
    gst = 0;
  const groups = new Map();
  items.forEach((item) => {
    const s = Number(item.stock) || 0;
    const offer = Number(item.offer) || 0;
    const mrp = Number(item.CPrice) || 0;
    const g = Number(item.GSTApplied) || 0;
    const lineNet = s * offer;
    const b = lineNet * (100 / (100 + g));
    const tax = b * (g / 100);
    net += lineNet;
    gross += s * mrp;
    dis += s * (Number(item.Disamt) || 0);
    qty += s;
    basic += b;
    gst += tax;
    if (!groups.has(g)) {
      groups.set(g, {
        GST: g,
        CGST_RATE: g / 2,
        Basic_amt: 0,
        CGST_AMT: 0,
        SGST: g / 2,
        SGST_AMT: 0,
        NET_AMT: 0,
      });
    }
    const row = groups.get(g);
    row.Basic_amt += b;
    row.CGST_AMT += tax / 2;
    row.SGST_AMT += tax / 2;
    row.NET_AMT += lineNet;
  });
  return {
    net,
    gross,
    dis,
    qty,
    basic,
    gst,
    groups: Array.from(groups.values()),
  };
};

const ExchangeBill = () => {
  const qrRef = useRef(null);
  const tagNoRef = useRef(null);
  const submitRef = useRef(null);
  const searchRef = useRef(null);
  const mobileRef = useRef(null);
  const lastItemRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scannedRef = useRef(false);
  // always hold the latest values so camera-scanner callbacks never read stale state
  const dataRef = useRef({
    barCodeData: [],
    exchangeData: [],
    qtyData: [],
    gstData: [],
  });
  const modeRef = useRef("SALE"); // active tab
  const dialogTargetRef = useRef("SALE"); // which list Home/Taans dialogs save into

  const [mode, setMode] = useState("SALE"); // "SALE" | "EXCHANGE"
  const [editTarget, setEditTarget] = useState("SALE");
  const [wasExchangeBill, setWasExchangeBill] = useState(false);
  const [exchangeData, setExchangeData] = useState([]);

  const [open, setOpen] = useState(false);
  const [userArea, setUserArea] = useState();
  const [userName, setUserName] = useState();
  const [singleImage, setSingleImage] = useState();
  const [gstNo, setGstNo] = useState("");
  const [gstValue, setGstValue] = useState();
  const [userPhone, setUserPhone] = useState("");
  const [ins, setIns] = useState("");
  const [ins1, setIns1] = useState("");
  const [ins2, setIns2] = useState("");
  const [ins3, setIns3] = useState("");
  const [ins4, setIns4] = useState("");
  const [ins5, setIns5] = useState("");
  const [ins6, setIns6] = useState("");
  const [address, setAddress] = useState("");
  const [address1, setAddress1] = useState("");
  const [city, setCity] = useState("");
  const [invoiceNo, setInvoiceNo] = useState(0);
  const [billDate, setBillDate] = useState();
  const [qrOpen, setQrOpen] = useState(false);
  const [scanOpen] = useState(false);
  const [qrCodeOpen, setQrCodeOpen] = useState(false);
  const [scanResult, setScanResult] = useState("");
  const [barCodeData, setBarCodeData] = useState([]);
  const [gstData, setGstData] = useState([]);
  const [customerArea, setCustomerArea] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [gst, setGst] = useState(0);
  const [gstAmt, setGstAmt] = useState(0);
  const [cGstRate, setCGstRate] = useState(0);
  const [cGstAmt, setCGstAmt] = useState(0);
  const [sGstRate, setSGstRate] = useState(0);
  const [sGstAmt, setSGstAmt] = useState(0);
  const [iGstRate, setIGstRate] = useState(0);
  const [iGstAmt, setIGstAmt] = useState(0);
  const [basicAmt, setBasicAmt] = useState(0);
  const [netAmt, setNetAmt] = useState(0);
  const [smCode, setSmCode] = useState("");
  const [modifyOpen, setModifyOpen] = useState(false);
  const [modifyCode, setModifyCode] = useState();
  const [cashOpen, setCashOpen] = useState(false);
  const [cashValue, setCashValue] = useState("CASH");
  const [cashAmt, setCashAmt] = useState(0);
  const [cardAmt, setCardAmt] = useState(0);
  const [upiAmt, setUpiAmt] = useState(0);
  const [onlineAmt, setOnlineAmt] = useState(0);
  const [creditAmt, setCreditAmt] = useState(0);
  const [cardAcc, setCardAcc] = useState("");
  const [upiAcc, setUpiAcc] = useState("");
  const [onlineAcc, setOnlineAcc] = useState("");
  const [switchCredit, setSwitchCredit] = useState("Cash");
  const [creditCashValue, setCreditCashValue] = useState("CREDIT");
  const [creditCashAmt, setCreditCashAmt] = useState(0);
  const [creditCardAmt, setCreditCardAmt] = useState(0);
  const [creditUpiAmt, setCreditUpiAmt] = useState(0);
  const [creditOnlineAmt, setCreditOnlineAmt] = useState(0);
  const [invNo, setInvNo] = useState(0); // set only when an existing bill is opened for modify
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const [whatsAppCheck, setWhatsAppCheck] = useState(false);
  const [base64Images, setBase64Images] = useState();
  const [base64QR, setBase64QR] = useState("");
  const [homeDetailsOpen, setHomeDetailsOpen] = useState(false);
  const [productData, setProductData] = useState([]);
  const [qty, setQty] = useState(0);
  const [amount, setAmount] = useState(0);
  const [disCount, setDisCount] = useState(0);
  const [rate, setRate] = useState(0);
  const [disAmt, setDisAmt] = useState(0);
  const [productName, setProductName] = useState("");
  const [designCode, setDesignCode] = useState("");
  const [pTypeName, setPTypeName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [taansOpen, setTaansOpen] = useState(false);
  const [taansData, setTaansData] = useState([]);
  const [qtyData, setQtyData] = useState([]);
  const [offerAOpen, setOfferAOpen] = useState(false);
  const [offerAData, setOfferAData] = useState([]);
  const [offerARate, setOfferARate] = useState(0);
  const [offerBOpen, setOfferBOpen] = useState(false);
  const [offerBData, setOfferBData] = useState([]);
  const [offerBRate, setOfferBRate] = useState(0);
  const [itemDetailsOpen, setItemDetailsOpen] = useState(false);
  const [itemDetailsData, setItemDetailsData] = useState({});
  const [itemDesignCode, setItemDEsignCode] = useState();
  const [messageApi, contextHolder] = message.useMessage();
  const [userDetails, setUserDetails] = useState({});
  const [phoneNo, setPhoneNo] = useState();
  const [custOpen, setCustOpen] = useState(false);
  const [comm, setComm] = useState(0);
  const [saving, setSaving] = useState(false);
  const [printSaving, setPrintSaving] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_CUSTOMER_FORM);

  const tenantName = localStorage.getItem("tenantName");
  const pdfModule = localStorage.getItem("pdfModule");
  const localIp = localStorage.getItem("ipAddress");
  const loginName = localStorage.getItem("loginName");
  const upiId = localStorage.getItem("upiId");
  const adminType = localStorage.getItem("adminType");
  const canEdit = adminType === "ADMIN" || (adminType === "OPERATOR" && !invNo);

  const H = { headers: { tenantName } };
  const JSON_H = {
    headers: { "Content-Type": "application/json", tenantName },
  };
  const getTable = (table, where) =>
    axios.get(
      `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNameWithWhere?tableName=${table}&where=${where}`,
      H,
    );

  /* ------------------------------ totals ------------------------------ */
  const saleT = calcTotals(barCodeData);
  const exT = calcTotals(exchangeData);
  const totalNetAmt = saleT.net - exT.net; // balance = bill - exchange
  const totalAmount = saleT.gross;
  const totalDisAmt = saleT.dis;
  const totalQty = saleT.qty;
  const totalBasicAmount = saleT.basic;
  const totalGstAmt = saleT.gst;
  const totalCGSTAmt = saleT.gst / 2;
  const totalSGSTAmt = saleT.gst / 2;

  // signed list for print / pdf helpers: exchange lines show as negative rows
  const printItems = [
    ...barCodeData,
    ...exchangeData.map((i) => ({
      ...i,
      stock: -(Number(i.stock) || 0),
      ProdName: `[EXCH] ${i.ProdName ?? ""}`,
    })),
  ];
  const printTotals = calcTotals(printItems);

  const totalAmt = totalNetAmt - cardAmt - upiAmt - onlineAmt - creditAmt;
  const invPaidAmt = totalNetAmt - creditAmt;
  const upiLink = `upi://pay?pa=${upiId}&am=${upiAmt}&cu=INR`;
  const heroCustomer = userDetails?.Custname || customerName;
  const isExchange = mode === "EXCHANGE";
  const activeItems = isExchange ? exchangeData : barCodeData;
  const hasItems = barCodeData.length > 0 || exchangeData.length > 0;
  // stock limits don't apply to returned (exchange) items
  const exchangeQtyData = exchangeData.map((i) => ({
    DesignCode: i.DesignCode,
    Qty: 999999,
  }));

  dataRef.current = { barCodeData, exchangeData, qtyData, gstData };
  modeRef.current = mode;

  const isValidValue = (val) =>
    val !== null &&
    val !== undefined &&
    val !== "-" &&
    String(val).trim() !== "" &&
    String(val) !== "undefined";

  const toastError = (text) =>
    messageApi.open({
      type: "error",
      content: (
        <span style={{ fontSize: "15px", fontWeight: 600 }}>{text}</span>
      ),
    });

  const toggleDrawer = () => setOpen(false);

  const changeMode = (m) => {
    setMode(m);
    modeRef.current = m;
    setScanResult("");
    setTimeout(() => tagNoRef.current?.focus(), 0);
  };

  /* ---------------------------- master data --------------------------- */

  const invoiceCountAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetMaxNumberInTable?tableName=CREDIT_BILLMAST&column=BILLNO`,
        H,
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const rawValue = data[0]?.Column1;
        const maxInvNo = Number.isFinite(Number(rawValue))
          ? Number(rawValue)
          : 0;
        const newInvNo = maxInvNo + 1;
        setInvoiceNo(newInvNo);
        return newInvNo;
      }
    } catch (error) {
      console.error("Error fetching invoice number:", error);
    }
  };

  const onlineAccNoAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=ONLINEMODE_MAST`,
        H,
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const card = data.find((item) => item.PTYPE === "CARD");
        const upi = data.find((item) => item.PTYPE === "UPI");
        const online = data.find((item) => item.PTYPE === "ONLINE");
        if (card) setCardAcc(card.accno);
        if (upi) setUpiAcc(upi.accno);
        if (online) setOnlineAcc(online.accno);
      }
    } catch (error) {
      console.error("Error fetching online accounts:", error);
    }
  };

  const gstCheckAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=PURCHASE_TYPE`,
        H,
      );
      if (Array.isArray(response.data) && response.data.length > 0) {
        setGstData(response.data);
      }
    } catch (error) {
      console.error("Error fetching GST config:", error);
    }
  };

  const commissionAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=CONFIGURE`,
        H,
      );
      if (Array.isArray(response.data) && response.data.length > 0) {
        setComm(response.data[0]?.COMM);
      }
    } catch (error) {
      console.error("Error fetching commission:", error);
    }
  };

  const userAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=FIRM_CONFIGURE`,
        H,
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setUserName(data[0]?.FIRMNAME);
        setUserArea(data[0]?.CITY);
        setSingleImage(data[0]?.EPASS2);
        setGstNo(data[0]?.TINNO);
        setGstValue(data[0]?.GST);
        setUserPhone(data[0]?.MOBILE);
        setIns(data[0]?.INST1);
        setIns1(data[0]?.INST2);
        setIns2(data[0]?.INST3);
        setIns3(data[0]?.INST4);
        setIns4(data[0]?.INST5);
        setIns5(data[0]?.INST6);
        setIns6(data[0]?.INST7);
        setAddress(data[0]?.ADD1);
        setAddress1(data[0]?.ADD2);
        setCity(data[0]?.CITY);
      }
    } catch (error) {
      console.error("Error fetching firm config:", error);
    }
  };

  /* ----------------------------- customers ---------------------------- */

  const customerToForm = (c) => ({
    mobile1: c?.Mobileno || "",
    city: c?.Area || "",
    name: c?.Custname || "",
    mobile2: c?.Phoneno || "",
    phone: c?.Phoneno || "",
    address1: c?.add1 || "",
    address2: c?.add2 || "",
    address3: c?.add3 || "",
    address4: c?.add4 || "",
    state: c?.STATE || "ANDHRA PRADESH",
    stateCode: c?.STATECODE || "37",
    gstin: c?.TINNO || "-",
    panNo: "",
    idProof: "",
    dob: c?.dob || "",
    anniversary: c?.ANNVERSARY || "",
    locationType: c?.STATION || "LOCAL",
  });

  // used by the "modify bill" flow – loads the customer silently
  const userGetDetailsAPI = async (value) => {
    const mobileValue = isValidValue(value) ? value : phoneNo;
    if (!isValidValue(mobileValue)) return;
    try {
      const response = await getTable("CUSTOMER", `MOBILENO='${mobileValue}'`);
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setUserDetails(data[0]);
        setFormValues(customerToForm(data[0]));
        setPhoneNo();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
    }
  };

  // used by the customer dialog – opens "new customer" when mobile is unknown
  const userDetailsAPI = async (value) => {
    const mobileValue = isValidValue(value) ? value : phoneNo;
    if (!isValidValue(mobileValue)) return;
    try {
      const response = await getTable("CUSTOMER", `MOBILENO='${mobileValue}'`);
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        setUserDetails(data[0]);
        setFormValues(customerToForm(data[0]));
        setPhoneNo();
      } else {
        setCustOpen(true);
        setUserDetails({});
        setFormValues({ ...EMPTY_CUSTOMER_FORM, mobile1: phoneNo });
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
    }
  };

  const createCustomerDetailsAPI = async (value) => {
    const requestBody = [
      {
        street: "-",
        custname: value?.name || "-",
        add1: value?.address1 || "-",
        add2: value?.address2 || "-",
        add3: value?.address3 || "-",
        area: value?.city || "-",
        phoneno: value?.phone || "-",
        mobileno: value?.mobile1 || "-",
        cardno: "",
        mstatus: "",
        occupation: "",
        education: "",
        college: "",
        discount: 0,
        dob: value?.dob || "",
        annversary: value?.anniversary || "",
        state: value?.state || "-",
        gender: "",
        district: "",
        website: "",
        category: "",
        fax: "-",
        add4: value?.address4 || "-",
        tinno: value?.gstin || "-",
        points: 0,
        statecode: value?.stateCode || "",
        station: value?.locationType || "",
        cloud_upload: false,
      },
    ];
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/InsertCustomer`,
        requestBody,
        JSON_H,
      );
      userDetailsAPI(value?.mobile1);
      setFormValues({});
      setCustOpen(false);
      tagNoRef.current?.focus();
    } catch (error) {
      console.error("Error posting customer:", error);
    }
  };

  const deleteCustomerDetailsAPI = async (mobile) => {
    try {
      await axios.post(
        `${CREATE_jwel}/api/ERP/DeleteDataFromGivenTableNameWithWhere?tableName=CUSTOMER&where=MOBILENO='${mobile}'`,
        {},
        H,
      );
      setUserDetails({});
      setCustOpen(false);
    } catch (error) {
      console.error(
        "Error deleting customer:",
        error.response?.data || error.message,
      );
    }
  };

  /* ------------------------------ items ------------------------------- */

  // honorAllowFlag=true  -> scanned / modified bills (uses item.GST when slab pricing is off)
  // honorAllowFlag=false -> items coming from the Home / Taans / Offer dialogs
  const getAppliedGST = (item, honorAllowFlag = true) => {
    const gstItem = dataRef.current.gstData.find((g) => g.PType === item.PTYPE);
    const offerAmount = item.offer ?? 0;
    const bySlab = () => {
      if (offerAmount <= gstItem.BELLOWVALUE) return gstItem.BELLOWPER;
      if (offerAmount >= gstItem.ABOVEVALUE) return gstItem.ABOVEPER;
      return 0;
    };
    if (honorAllowFlag) {
      if (gstItem?.ALLOWGSTVALUE === true) return bySlab();
      return item?.GST;
    }
    return gstItem ? bySlab() : 0;
  };

  const qtyAPI = async (no) => {
    try {
      const response = await getTable("CURRENT_STOCK", `DESIGNCODE='${no}'`);
      const data = response.data;
      setQtyData((prev) => {
        const fresh = data
          .filter((item) => !prev.some((p) => p.DesignCode === item.DesignCode))
          .map((item) => ({ DesignCode: item.DesignCode, Qty: item.Qty }));
        return [...prev, ...fresh];
      });
    } catch (error) {
      console.error("Error fetching stock qty:", error);
    }
  };

  // Scans a design code into the ACTIVE mode (SALE or EXCHANGE)
  const mainAPI = async (code) => {
    const tag = code || scanResult;
    const target = modeRef.current;
    try {
      const response = await getTable("CURRENT_STOCK", `DESIGNCODE='${tag}'`);
      const data = response.data;
      setScanResult("");
      if (!Array.isArray(data) || data.length === 0) {
        toastError("Design Code Not existed");
        return;
      }
      dialogTargetRef.current = target;

      if (data[0]?.taans === true) {
        setTaansOpen(true);
        setTaansData(data.map((item) => ({ ...item, stock: 0 })));
        return;
      }

      // ---------- EXCHANGE: item comes back into stock, no qty limits / offers ----------
      if (target === "EXCHANGE") {
        const added = data.map((newItem) => ({
          ...newItem,
          stock: 1,
          GSTApplied: getAppliedGST(newItem, true),
          SMCODE: "",
        }));
        dataRef.current.exchangeData = [
          ...dataRef.current.exchangeData,
          ...added,
        ];
        setExchangeData((prev) => [...prev, ...added]);
        return;
      }

      // ---------- SALE ----------
      const latest = dataRef.current;
      const newQtyData = [...latest.qtyData];
      data.forEach((item) => {
        if (!newQtyData.some((q) => q.DesignCode === item.DesignCode)) {
          newQtyData.push({ DesignCode: item.DesignCode, Qty: item.Qty });
        }
      });
      setQtyData(newQtyData);
      dataRef.current.qtyData = newQtyData;

      if (data[0]?.SMODEL === "A") {
        setOfferAOpen(true);
        setOfferARate(data[0]?.CPrice);
        setOfferAData(
          data.map((item) => ({
            ...item,
            stock: 3,
            CPrice: data[0]?.OQTY3 / 3,
            offer: data[0]?.OQTY3 / 3,
          })),
        );
        return;
      }
      if (data[0]?.SMODEL === "B") {
        setOfferBOpen(true);
        setOfferBRate(data[0]?.Disper);
        setOfferBData(
          data.map((item) => ({
            ...item,
            stock: 3,
            Disper: data[0]?.OQTY3,
            disAmt: (data[0]?.CPrice * data[0]?.OQTY3) / 100,
            offer: data[0]?.CPrice - (data[0]?.CPrice * data[0]?.OQTY3) / 100,
          })),
        );
        return;
      }

      const working = [...latest.barCodeData];
      data.forEach((newItem) => {
        const dc = newItem.DesignCode;
        const qtyInfo = newQtyData.find((q) => q.DesignCode === dc);
        if (!qtyInfo || Number(qtyInfo.Qty) === 0) {
          toastError(`Qty is zero for ${dc}.`);
          return;
        }
        const used = working
          .filter((i) => i.DesignCode === dc)
          .reduce((s, i) => s + (Number(i.stock) || 0), 0);
        if (used >= Number(qtyInfo.Qty)) {
          toastError(`Qty limit reached for ${dc}.`);
          return;
        }
        working.push({
          ...newItem,
          stock: 1,
          GSTApplied: getAppliedGST(newItem, true),
        });
      });
      dataRef.current.barCodeData = working;
      setBarCodeData(working);
    } catch (error) {
      console.error("Error fetching design code:", error);
    }
  };

  const homeBillData = async (code) => {
    const tag = code || scanResult;
    try {
      const response = await getTable("PRODUCT_MASTER", `SHORTKEY='${tag}'`);
      const data = response.data;
      if (!Array.isArray(data) || data.length === 0) {
        setScanResult("");
        return;
      }
      dialogTargetRef.current = modeRef.current;
      setScanResult("");
      setProductData(data);
      setDesignCode(data[0]?.SHORTKEY);
      setHomeDetailsOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  // shared by the Home / Taans / Offer A / Offer B dialogs; target = "SALE" | "EXCHANGE"
  const addDialogItems = (items, target = "SALE") => {
    if (target === "EXCHANGE") {
      setExchangeData((prev) => [
        ...prev,
        ...items.map((it) => ({
          ...it,
          GSTApplied: getAppliedGST(it, false),
          SMCODE: "",
        })),
      ]);
      return;
    }
    const valid = [];
    items.forEach((it) => {
      if (Number(it.stock) > Number(it.Qty)) {
        toastError(`Stock exceeded for Design Code ${it.DesignCode}`);
        return;
      }
      valid.push({ ...it, GSTApplied: getAppliedGST(it, false) });
    });
    if (valid.length) setBarCodeData((prev) => [...prev, ...valid]);
  };

  const checkQtyThen = (stock, fn) => {
    if (Number(stock) === 0) {
      messageApi.open({
        type: "error",
        content: <div style={{ fontSize: "18px" }}>Check Quantity</div>,
      });
      return;
    }
    fn();
  };

  const handleHomeBillSave = () => {
    if (Number(qty) === 0) {
      messageApi.open({
        type: "error",
        content: <div style={{ fontSize: "18px" }}>Check Quantity</div>,
      });
      return;
    }
    const homeData = [
      {
        ACITY: "-",
        AGENTNAME: "-",
        Area: "-",
        BOXES: null,
        BRANCHNAME: "-",
        CASERATECP: null,
        CASERATEDP: null,
        CASERATERP: null,
        CGSTAMT: 0,
        CGSTRATE: 0,
        COLOUR: "-",
        CPrice: rate,
        CTOKEN: 0,
        Code: "-",
        CompName: brandName,
        DPrice: 0,
        DesignCode: designCode,
        Disamt: disAmt,
        Disper: disCount,
        ECODE: "-",
        ENTRYDATE: "-",
        ENTRYNO: 0,
        EQTY: 0,
        EXPDATE: null,
        FITTING: "-",
        FLOOR: "-",
        FYEAR: "-",
        GST: 0,
        GSTAMT: 0,
        GrosDPrice: 0,
        GroupName: "-",
        HID: 0,
        HSNCODE: hsnCode,
        ICODETYPE: "-",
        IGSTAMT: 0,
        IGSTRATE: 0,
        InvNO: "-",
        Invdate: "-",
        ItemCode: "0",
        LASTBILLDATE: "-",
        LENGTH: null,
        LOOSEPCS: null,
        LQTY: null,
        OFFER_DIS: 0,
        OQTY2: 0,
        OQTY3: 0,
        OQTY4: null,
        PACKPCS: null,
        PNTMODEL: "-",
        PQTY: 0,
        PTYPE: pTypeName,
        Partyname: "-",
        ProdName: productName,
        Prodsize: "-",
        Qty: qty,
        RESELLERPRICE: null,
        RQTY: null,
        SEMIWSALERPRICE: null,
        SET_ITEMS: false,
        SGSTAMT: 0,
        SGSTRATE: 0,
        SLEVES: "-",
        SMODEL: "-",
        SNO: 0,
        TOKEN: 0,
        USTATUS: null,
        WPER: 0,
        WPRICE: 0,
        stock: qty,
        cloud_upload: false,
        mesure: null,
        offer: amount,
        taans: false,
      },
    ];
    const target = dialogTargetRef.current;
    setHomeDetailsOpen(false);
    setQty(0);
    setAmount(0);
    setDisCount(0);
    setRate(0);
    setDisAmt(0);
    setProductName("");
    setDesignCode("");
    setPTypeName("");
    setHsnCode("");
    setBrandName("");
    setProductData([]);
    addDialogItems(homeData, target);
  };

  const handleTaansBillSave = () =>
    checkQtyThen(taansData[0]?.stock, () => {
      const target = dialogTargetRef.current;
      setTaansOpen(false);
      setTaansData([]);
      addDialogItems(taansData, target);
    });

  const handleOfferABillSave = () =>
    checkQtyThen(offerAData[0]?.stock, () => {
      setOfferAOpen(false);
      setOfferAData([]);
      addDialogItems(offerAData, "SALE");
    });

  const handleOfferBBillSave = () =>
    checkQtyThen(offerBData[0]?.stock, () => {
      setOfferBOpen(false);
      setOfferBData([]);
      addDialogItems(offerBData, "SALE");
    });

  const handleDelete = (indexToDelete, target = "SALE") => {
    tagNoRef.current?.focus();
    const setter = target === "EXCHANGE" ? setExchangeData : setBarCodeData;
    setter((prev) => prev.filter((_, i) => i !== indexToDelete));
    setCashValue("CASH");
    setCardAmt(0);
    setUpiAmt(0);
    setOnlineAmt(0);
    setCreditAmt(0);
  };

  /* -------------------- modify an existing invoice -------------------- */

  const modifyOnlineAPI = async (no) => {
    const code = no ?? modifyCode;
    try {
      const response = await getTable(
        "ORG_CARDDET",
        `BILLNO=${code} AND STYPE='BILLING'`,
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const ptype = data[0]?.PTYPE;
        const amt = data[0]?.AMT;
        setCashAmt(0);
        setCardAmt(0);
        setUpiAmt(0);
        setOnlineAmt(0);
        setCreditAmt(0);
        if (ptype === "CARD") {
          setCashValue("CARD");
          setCardAmt(amt);
        } else if (ptype === "UPI") {
          setCashValue("UPI");
          setUpiAmt(amt);
        } else if (ptype === "ONLINE") {
          setCashValue("ONLINE");
          setOnlineAmt(amt);
        } else {
          setCashValue("CASH");
        }
      }
    } catch (error) {
      console.error("Error fetching online payment:", error);
    }
  };

  const modifyOpeningLedgerAPI = async (no) => {
    const code = no ?? modifyCode;
    try {
      const response = await getTable(
        "TRANS_ENTRY_DATA_CUST",
        `ENTRYNO=${code} AND SALESTYPE='BILLING'`,
      );
      const data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        // normal bills store credit in JAMA, exchange bills (desktop format) in NAMA
        const credit =
          Number(data[0]?.NAMA) > 0
            ? Number(data[0]?.NAMA)
            : Number(data[0]?.JAMA) > 0
              ? Number(data[0]?.JAMA)
              : 0;
        if (credit > 0) {
          setCashValue("CREDIT");
          setCreditAmt(credit);
          setCardAmt(0);
          setUpiAmt(0);
          setOnlineAmt(0);
        }
        setCustomerName(data[0]?.PARTYNAME ?? "");
        setCustomerArea(data[0]?.AREA ?? "");
        setCustomerMobile(data[0]?.AGENTNAME ?? "");
      }
    } catch (error) {
      console.error("Error fetching ledger:", error);
    }
  };

  const modifyMastAPI = async (no) => {
    const code = no ?? modifyCode;
    try {
      const response = await getTable("CREDIT_BILLMAST", `BILLNO=${code}`);
      const data = response?.data;
      if (response?.status === 200 && data?.[0]) {
        setCustomerMobile(data[0]?.PhoneNo);
        setCustomerName(data[0]?.CustName);
        setCustomerArea(data[0]?.Street);
        userGetDetailsAPI(data[0]?.PhoneNo);
      }
    } catch (error) {
      console.error("Error fetching bill master:", error);
    }
  };

  // BILL_ITEMS_DET row -> cart item. ex=true for TYPE='EB' (exchange) rows
  const mapDetRow = (item, ex) => ({
    ACITY: "-",
    AGENTNAME: "-",
    Area: item?.AREA,
    BOXES: item?.BOXES,
    BRANCHNAME: "-",
    CASERATECP: null,
    CASERATEDP: null,
    CASERATERP: null,
    CGSTAMT: item?.CGSTAMT,
    CGSTRATE: item?.CGSTRATE,
    COLOUR: item?.COLOUR,
    CPrice: item?.rate,
    CTOKEN: 0,
    Code: item?.code,
    CompName: item?.CompName,
    DPrice: item?.DPrice,
    DesignCode: item?.ITEMCODE,
    Disamt: item?.ITEM_DIS,
    Disper: item?.DISPER,
    ECODE: item?.ECODE,
    ENTRYDATE: "-",
    ENTRYNO: 0,
    EQTY: 0,
    EXPDATE: null,
    FITTING: item?.FITTING,
    FLOOR: item?.FLOOR,
    FYEAR: "-",
    GST: item?.GST,
    GSTAMT: item?.GSTAMT,
    GrosDPrice: item?.ITEM_ACTRATE,
    GroupName: item?.GroupName,
    HID: item?.HID,
    HSNCODE: item?.HSNCODE,
    ICODETYPE: "-",
    IGSTAMT: item?.IGSTAMT,
    IGSTRATE: item?.IGSTRATE,
    InvNO: item?.INVNO,
    Invdate: item?.INVDATE,
    ItemCode: "0",
    LASTBILLDATE: "-",
    LENGTH: null,
    LOOSEPCS: null,
    LQTY: null,
    OFFER_DIS: 0,
    OQTY2: ex ? 0 : Number(item?.PCSPRICE),
    OQTY3: ex ? 0 : Number(item?.CASEPRICE),
    OQTY4: null,
    PACKPCS: null,
    PNTMODEL: "-",
    PQTY: 0,
    PTYPE: item?.PTYPE,
    Partyname: item?.PARTYNAME,
    ProdName: item?.itemname,
    Prodsize: item?.PRODSIZE,
    Qty: ex ? Math.abs(Number(item?.qty)) : item?.qty,
    RESELLERPRICE: null,
    RQTY: null,
    SEMIWSALERPRICE: null,
    SET_ITEMS: false,
    SGSTAMT: item?.SGSTAMT,
    SGSTRATE: item?.SGSTRATE,
    SLEVES: item?.SLEVES,
    SMODEL: ex ? "-" : item?.STATION,
    SMCODE: item?.SMCODE ? String(item.SMCODE) : "",
    SNO: 0,
    TOKEN: item?.TOKEN,
    USTATUS: null,
    WPER: 0,
    WPRICE: 0,
    stock: ex ? Math.abs(Number(item?.qty)) : item?.qty,
    cloud_upload: false,
    mesure: null,
    offer: item?.ITEM_ACTRATE,
    taans: false,
  });

  const modifyExchangeDetAPI = async (no) => {
    try {
      const response = await getTable(
        "BILL_ITEMS_DET",
        `BILLNO=${no} AND TYPE='EB'`,
      );
      const data = response?.data;
      if (response?.status !== 200 || !Array.isArray(data) || !data.length)
        return;
      setWasExchangeBill(true);
      setExchangeData((prev) => [
        ...prev,
        ...data.map((row) => {
          const it = mapDetRow(row, true);
          return { ...it, GSTApplied: getAppliedGST(it, true) };
        }),
      ]);
    } catch (error) {
      console.error("Error fetching exchange items:", error);
    }
  };

  const modifyDetAPI = async (no) => {
    const code = no ?? modifyCode;
    try {
      const response = await getTable(
        "BILL_ITEMS_DET",
        `BILLNO=${code} AND TYPE='B'`,
      );
      const data = response?.data;
      if (response?.status !== 200 || !Array.isArray(data)) return;

      let aRate = 0;
      let bRate = 0;
      data.forEach((item) => {
        if (item.STATION === "A") aRate = item.CESSPER;
        else if (item.STATION === "B") bRate = item.CESSPER;
      });
      setOfferARate(aRate);
      setOfferBRate(bRate);

      const modifiedData = data.map((item) => mapDetRow(item, false));

      setModifyOpen(false);
      setModifyCode();
      setInvNo(data[0]?.billno ?? Number(code));
      setBillDate(data[0]?.billdate);
      for (const item of modifiedData) {
        if (item.DesignCode) await qtyAPI(item.DesignCode);
      }
      setBarCodeData((prev) => [
        ...prev,
        ...modifiedData.map((item) => ({
          ...item,
          GSTApplied: getAppliedGST(item, true),
        })),
      ]);
      await modifyExchangeDetAPI(code);
    } catch (error) {
      console.error("Error fetching bill items:", error);
    }
  };

  /* ------------------------------ effects ----------------------------- */

  useEffect(() => {
    setCashAmt(totalNetAmt - cardAmt - upiAmt - onlineAmt - creditAmt);
  }, [totalNetAmt, cardAmt, upiAmt, onlineAmt, creditAmt]);

  useEffect(() => {
    if (!totalNetAmt) return;
    const canvas = qrRef.current?.querySelector("canvas");
    if (canvas) setBase64QR(canvas.toDataURL("image/png"));
  }, [totalNetAmt, upiLink]);

  useEffect(() => {
    lastItemRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [barCodeData, exchangeData, mode]);

  useEffect(() => {
    gstCheckAPI();
    invoiceCountAPI();
    userAPI();
    onlineAccNoAPI();
    commissionAPI();
  }, []);

  /* ------------------- save / update (desktop APIs) ------------------- */

  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  const billNoDate = new Date(
    dayjs(billDate).startOf("day").format("YYYY-MM-DD"),
  ).toISOString();

  const buildBillPayload = (isUpdate, asExchange) => {
    const billNoValue = isUpdate ? Number(invNo) : 0;
    const billDateStr = billDate
      ? dayjs(billDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    const masterDate = billDate ? billNoDate : date.toISOString();
    const smc = (item) => (item?.SMCODE ? String(item.SMCODE) : "-");
    const calc = (item) => {
      const basic = item.stock * item.offer * (100 / (100 + item.GSTApplied));
      const commission = (Number(item.stock * item.offer) * comm) / 100;
      return { basic, commToken: item?.TOKEN > 0 ? 0 : commission };
    };

    const billData = barCodeData.map((item, index) => {
      const { basic, commToken } = calc(item);
      return {
        billno: billNoValue,
        billdate: billDateStr,
        street: userDetails?.Area || "-",
        custName: userDetails?.Custname || "-",
        sno: index + 1,
        itemcode: item?.DesignCode || "",
        itemname: item?.ProdName || "",
        code: item?.Code || "",
        compName: item?.CompName || "",
        groupName: item?.GroupName || "",
        smcode: smc(item),
        qty: item?.stock || 0,
        rate: item?.CPrice || 0,
        disper: item?.Disper || 0,
        disAmt: item?.Disamt || 0,
        actrate: item?.offer || 0,
        amount: item?.offer * item?.stock || 0,
        hid: item?.HID || 0,
        dPrice: item?.DPrice || 0,
        comm: Number(commToken) || 0,
        token: item?.TOKEN || 0,
        ptype: item?.PTYPE || "",
        area: item?.Area || "",
        partyname: item?.Partyname || "",
        vatper: 0,
        vatamt: 0,
        floor: item?.FLOOR || "",
        estno: "-",
        station: "mobile",
        hsncode: item?.HSNCODE || "",
        gst: item?.GSTApplied || 0,
        gstamt: basic * (item?.GSTApplied / 100) || 0,
        cgstrate: item?.GSTApplied / 2 || 0,
        cgstamt: (basic * (item?.GSTApplied / 100)) / 2 || 0,
        sgstrate: item?.GSTApplied / 2 || 0,
        sgstamt: (basic * (item?.GSTApplied / 100)) / 2 || 0,
        igstrate: item?.IGSTRATE || 0,
        igstamt: item?.IGSTAMT || 0,
        basicamt: Number(basic) || 0,
        serialno: 0,
        brandcode: "0",
        salestype: "BILLING",
        prodsize: item?.Prodsize || "",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: item?.stock || 0,
        refcode: "-",
        boxes: 0,
        loosepcs: 0,
        pfcharges: 0,
        cessper: 0,
        cessamt: 0,
        packpcs: 0,
        caseprice: 0,
        pcsprice: 0,
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: item?.TOKEN || 0,
        ustatus: false,
        estbillno: false,
        cloud_upload: true,
        billreduce: 0,
        ctype: "RETAIL",
      };
    });

    // BILL_ITEMS_DET row. ex=true -> exchange row (type EB / salestype EXCHANGE)
    const detRow = (item, index, ex) => {
      const { basic, commToken } = calc(item);
      const offerRate = ex
        ? 0
        : item?.SMODEL === "A"
          ? offerARate
          : item?.SMODEL === "B"
            ? offerBRate
            : 0;
      return {
        billno: billNoValue,
        billdate: billDateStr,
        street: userDetails?.Area || "-",
        custName: userDetails?.Custname || "-",
        sno: index + 1,
        itemcode: String(item?.DesignCode ?? ""),
        itemname: String(item?.ProdName ?? ""),
        code: String(item?.Code ?? ""),
        compName: String(item?.CompName ?? ""),
        groupName: String(item?.GroupName ?? ""),
        smcode: smc(item),
        qty: Number(item?.stock) || 0,
        rate: Number(item?.CPrice) || 0,
        disper: Number(item?.Disper) || 0,
        disAmt: Number(item?.Disamt) || 0,
        actrate: String(item?.offer ?? ""),
        amount: String(item?.offer * item?.stock || ""),
        hid: Number(item?.HID) || 0,
        dPrice: Number(item?.DPrice) || 0,
        comm: Number(commToken) || 0,
        token: Number(item?.TOKEN) || 0,
        ptype: String(item?.PTYPE ?? ""),
        area: String(item?.Area ?? ""),
        partyname: String(item?.Partyname ?? ""),
        type: ex ? "EB" : "B",
        vatper: 0,
        vatamt: 0,
        floor: String(item?.FLOOR ?? ""),
        wsalebill: false,
        appno: 0,
        invno: String(item?.InvNO ?? ""),
        invdate: billDateStr,
        colour: String(item?.COLOUR ?? ""),
        fitting: String(item?.FITTING ?? ""),
        sleves: String(item?.SLEVES ?? ""),
        ecode: String(item?.ECODE ?? ""),
        prodsize: String(item?.Prodsize ?? ""),
        station: ex ? "-" : String(item?.SMODEL ?? "-"),
        iteM_DIS: Number(item?.Disamt) || 0,
        iteM_ACTRATE: Number(item?.offer) || 0,
        scheck: false,
        hsncode: String(item?.HSNCODE ?? ""),
        gst: Number(item?.GSTApplied) || 0,
        gstamt: basic * (item?.GSTApplied / 100) || 0,
        cgstrate: item?.GSTApplied / 2,
        cgstamt: (basic * (item?.GSTApplied / 100)) / 2 || 0,
        sgstrate: item?.GSTApplied / 2,
        sgstamt: (basic * (item?.GSTApplied / 100)) / 2 || 0,
        igstrate: 0,
        igstamt: 0,
        basicamt: Number(basic) || 0,
        serialno: 0,
        brandcode: "0",
        salestype: ex ? "EXCHANGE" : "BILLING",
        fsize: "-",
        tsize: "-",
        differ: "-",
        actqty: Number(item?.stock) || 0,
        refcode: "-",
        state: userDetails?.STATE || "",
        statecode: userDetails?.STATECODE || "",
        tinno: "-",
        idproof: "-",
        panno: "-",
        boxes: Number(item?.BOXES) || 0,
        loosepcs: Number(item?.LOOSEPCS) || 0,
        pfcharges: 0,
        cessper: Number(offerRate),
        cessamt: 0,
        packpcs: Number(item?.PACKPCS) || 0,
        caseprice: ex ? 0 : Number(item?.OQTY3) || 0,
        pcsprice: ex ? 0 : Number(item?.OQTY2) || 0,
        casepricers: 0,
        pcspricers: 0,
        measurementtype: "-",
        tokenper: 0,
        ustatus: false,
        gstin: "-",
        estbillno: false,
        cloud_upload: true,
        billreduce: 0,
      };
    };

    const billItemsDet = barCodeData.map((item, i) => detRow(item, i, false));
    const exchangeItemsDet = exchangeData.map((item, i) =>
      detRow(item, i, true),
    );

    const orgBillData = barCodeData.map((item) => ({
      billno: billNoValue,
      billdate: billDateStr,
      billtype: "RETAIL",
      itemcode: "0",
      designcode: String(item?.DesignCode ?? ""),
      qty: Number(item?.stock) || 0,
      rate: Number(item?.CPrice) || 0,
      brandcode: "0",
      salestype: "BILLING",
      hid: Number(item?.HID) || 0,
      cloud_upload: true,
    }));

    const master = {
      billno: billNoValue,
      billdate: masterDate,
      street: userDetails?.Area || "-",
      custName: userDetails?.Custname || "-",
      add1: userDetails?.add1 || "-",
      phoneNo: userDetails?.Mobileno || "-",
      totamt: Number(totalAmount),
      discount: Number(totalDisAmt),
      advAmt: Number(cashAmt),
      finalamt: Number(creditAmt),
      billtype: cashValue === "CREDIT" ? "CREDIT" : "CASH",
      lessAmt: 0,
      smcode: "-",
      pstatus: false,
      ebillno: 0,
      mode: cashValue === "CREDIT" || cashValue === "CASH" ? "CASH" : "CARD",
      accNO: "-",
      cardno: cashValue === "CREDIT" || cashValue === "CASH" ? "0" : "-",
      cardamt: Number(cardAmt || upiAmt || onlineAmt || 0),
      sTax: 0,
      flrcode: "-",
      esmCode: "-",
      days: 0,
      cStatus: true,
      card: 0,
      hid: 0,
      estno: 0,
      adD2: userDetails?.add2 || "-",
      billtime: masterDate,
      uname: loginName ?? "-",
      cancell: "NO",
      ctime: masterDate,
      cuname: "-",
      station: "mobile",
      billstatus: false,
      funame: "-",
      fstation: "-",
      reccash: 0,
      refcash: 0,
      wsalebill: false,
      appno: asExchange ? 1 : 0, // APPNO=1 marks exchange bills in the bill history
      ccause: "-",
      edisamt: 0,
      chqdate: masterDate,
      narration: "-",
      invpayableamt: Number(totalNetAmt),
      invpaidamt: Number(invPaidAmt),
      invrtnamt: 0,
      invbalamt: Number(creditAmt),
      invstatus: true,
      extradiscount: 0,
      prevpoints: 0,
      prevamt: 0,
      billpoints: 0,
      billvalue: 0,
      redimpoints: 0,
      redimamt: 0,
      totpoints: 0,
      totvalue: 0,
      prevcardno: "-",
      adjustments: 0,
      totgst: 0,
      totogstamt: Number(totalGstAmt),
      totcgstrate: 0,
      totcgstamt: Number(totalCGSTAmt),
      totsgstrate: 0,
      totsgstamt: Number(totalSGSTAmt),
      totigstrate: 0,
      totigstamt: 0,
      serialno: 0,
      salestype: "BILLING",
      custstation: userDetails?.STATION || "LOCAL",
      transport: "-",
      custtype: "CUSTOMER",
      stype: "BILLING",
      lrno: "-",
      lrdate: "-",
      shipstreet: "-",
      shipcustname: "-",
      shipadD1: "-",
      shipadD2: "-",
      shipphoneno: "-",
      shiptinno: "-",
      noofbags: "-",
      orderno: "-",
      orderdate: "-",
      dateofsupply: "-",
      placeofsupply: "-",
      company: "-",
      place: "-",
      branch: "-",
      bankaccno: "-",
      ifsccode: "-",
      state: "-",
      statecode: "-",
      shipstate: "-",
      shipstatecode: "-",
      bankname: "-",
      beatarea: "-",
      orddate: "-",
      gstin: "-",
      totbasicamt: Number(totalBasicAmount),
      totamtaftax: 0,
      salesper: 0,
      salesamt: 0,
      grossamt: Number(totalNetAmt),
      roundoff: 0,
      netamt: Number(totalNetAmt),
      tinno: "-",
      idproof: "-",
      panno: "-",
      purchno: "-",
      disamt: 0,
      schemeamt: 0,
      disper: 0,
      pinvno: "-",
      m_BTYPE: "-",
      m_BNO: "-",
      m_BDATE: "-",
      includinggst: false,
      acity: "-",
      agentname: "-",
      ustatus: false,
      estbillno: false,
      paymode: "-",
      totqty: totalQty,
      refundamt: 0,
      paidcard: Number(cardAmt),
      paidcheque: 0,
      paidonline: Number(onlineAmt),
      paidupi: Number(upiAmt),
      paidcash: Number(cashAmt),
      balance: Number(creditAmt),
      ctype: "RETAIL",
      withgst: false,
      marginper: 0,
      cloud_upload: true,
      couponredem: 0,
    };

    // ---- accounting entries ----
    const commonFields = {
      entrydate: billDateStr,
      entryno: billNoValue,
      vno: String(billNoValue),
      vdate: billDateStr,
      scode: 4,
      description: "-",
      salestype: "BILLING",
      hid: 0,
      sno: 0,
      clouD_UPLOAD: true,
      outstandinG_CHEQUE: "-",
    };
    const bankLedger =
      cashValue === "CARD" ? cardAcc : cashValue === "UPI" ? upiAcc : onlineAcc;
    const bankAmount =
      cashValue === "CARD"
        ? Number(cardAmt)
        : cashValue === "UPI"
          ? Number(upiAmt)
          : Number(onlineAmt);
    const entry = (over) => ({
      ...commonFields,
      subgroup: "-",
      maingroup: "",
      particulars: "",
      paymode: "-",
      ...over,
    });
    const isOnline =
      cashValue === "CARD" || cashValue === "UPI" || cashValue === "ONLINE";

    let singleEntryData = [];
    if (cashValue === "CASH") {
      singleEntryData = [
        entry({
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          jamaamount: totalNetAmt,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH SALES",
        }),
        entry({
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          maingroup: "CASH BOOK",
          jamaamount: totalNetAmt,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
        }),
      ];
    } else if (isOnline && totalAmt === 0) {
      singleEntryData = [
        entry({
          ledgername: "ONLINE SALES",
          groupname: "CUSTOMER",
          jamaamount: totalNetAmt,
          namaamount: 0,
          tranS_TYPE: "ONLINE SALES",
          stype: "ONLINE SALES",
        }),
        entry({
          ledgername: bankLedger,
          groupname: "CUSTOMER",
          jamaamount: 0,
          namaamount: bankAmount,
          tranS_TYPE: "ONLINE SALES",
          stype: "BANK TRANS",
          paymode: cashValue,
        }),
      ];
    } else if (isOnline && totalAmt > 0) {
      singleEntryData = [
        entry({
          ledgername: "ONLINE SALES",
          groupname: "CUSTOMER",
          jamaamount: totalNetAmt,
          namaamount: 0,
          tranS_TYPE: "ONLINE SALES",
          stype: "ONLINE SALES",
        }),
        entry({
          ledgername: bankLedger,
          groupname: "CUSTOMER",
          jamaamount: 0,
          namaamount: bankAmount,
          tranS_TYPE: "CASH SALES",
          stype: "BANK TRANS",
          paymode: cashValue,
        }),
        entry({
          ledgername: "CASH BOOK",
          groupname: "CASH BOOK",
          maingroup: "CASH BOOK",
          jamaamount: totalNetAmt - cardAmt - upiAmt - onlineAmt - creditAmt,
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH BOOK",
        }),
      ];
    } else if (cashValue === "CREDIT" && creditAmt > 0) {
      singleEntryData = [
        entry({
          ledgername: "CASH SALES",
          groupname: "CUSTOMER",
          jamaamount: Number(totalNetAmt),
          namaamount: 0,
          tranS_TYPE: "CASH SALES",
          stype: "CASH SALES",
        }),
        ...(cashAmt > 0
          ? [
              entry({
                ledgername: "CASH BOOK",
                groupname: "CASH BOOK",
                maingroup: "CASH BOOK",
                jamaamount: Number(cashAmt),
                namaamount: 0,
                tranS_TYPE: "CASH SALES",
                stype: "CASH BOOK",
              }),
            ]
          : []),
        entry({
          ledgername: userDetails?.Custname,
          groupname: "CUSTOMER",
          maingroup: "CASH BOOK",
          jamaamount: 0,
          namaamount: Number(creditAmt),
          tranS_TYPE: "CASH SALES",
          stype: "CUSTOMER DUE",
        }),
      ];
    }

    const orgCardDet = isOnline
      ? [
          {
            billno: billNoValue,
            billdate: masterDate,
            bal: Number(totalNetAmt),
            sno: 1,
            paymode: cashValue,
            accno: bankLedger,
            narr: "-",
            amt: bankAmount,
            cntrname: "",
            stype: "BILLING",
            ptype: cashValue,
            cloud_upload: true,
          },
        ]
      : [];

    const ledgerBase = {
      area: userDetails?.Area || "-",
      partyname: userDetails?.Custname || "-",
      billno: String(billNoValue),
      billdate: masterDate,
      entrydate: masterDate,
      particulars: "SALE INVOICE",
      narration: "-",
      hid: 0,
      agentcity: "-",
      agentname: String(userDetails?.Mobileno || "-"),
      tranS_TYPE: "SALES INVOICE",
      cusT_TYPE: "CUSTOMER",
      sno: 0,
      pstatus: "",
      salestype: "BILLING",
      chequeno: "-",
      chqdate: masterDate,
      chqamt: 0,
      chqstatus: false,
      accno: "-",
      chqcancell: "NO",
      description: "-",
      cloud_upload: false,
    };
    // exchange bills use the desktop ledger format (NAMA = customer owes), sale bills keep JAMA
    const transEntryDataCust =
      creditAmt > 0
        ? [
            asExchange
              ? {
                  ...ledgerBase,
                  entryno: billNoValue,
                  jamaqty: 0,
                  namaqty: Number(totalQty) || 0,
                  jama: 0,
                  nama: Number(creditAmt) || 0,
                }
              : {
                  ...ledgerBase,
                  entryno: 0,
                  jamaqty: Number(totalQty) || 0,
                  namaqty: 0,
                  jama: Number(creditAmt) || 0,
                  nama: 0,
                },
          ]
        : [];

    if (asExchange) {
      return {
        master,
        billData,
        billItemsDet,
        exchangeItemsDet,
        singleEntryData,
        orgCardDet,
        transEntryDataCust,
      };
    }
    return {
      holdbillno: 0,
      master,
      billData,
      billItemsDet,
      orgBillData,
      singleEntryData,
      orgCardDet,
      transEntryDataCust,
    };
  };

  // one call to the combined endpoint – the server writes every table
  const submitBill = async () => {
    const isUpdate = Boolean(invNo);
    const asExchange = exchangeData.length > 0 || wasExchangeBill;
    const url = asExchange
      ? `${CREATE_jwel}/api/ExchangeBill/${isUpdate ? "Update" : "Save"}`
      : `${CREATE_jwel}/api/BillGeneration/${isUpdate ? "Update" : "Save"}`;
    const response = await axios.post(
      url,
      buildBillPayload(isUpdate, asExchange),
      JSON_H,
    );
    if (response?.data?.isInsert === true) {
      handleRest();
      return true;
    }
    toastError("Bill could not be saved. Please try again.");
    return false;
  };

  const validateBill = () => {
    tagNoRef.current?.focus();
    if (!hasItems) {
      toastError("Please add Sale or Exchange items.");
      return false;
    }
    if (cashValue === "CREDIT" && Object.keys(userDetails || {}).length === 0) {
      toastError("Please enter customer details for CREDIT bill.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (saving || !validateBill()) return;
    try {
      setSaving(true);
      if (whatsAppCheck === true) {
        const nextInvNo = await invoiceCountAPI();
        const pdf = await handlePdfDownload(nextInvNo);
        await sendWhatsAppAPI(pdf);
      }
      await submitBill();
    } catch (error) {
      console.error("Error creating bill:", error);
      toastError("Something went wrong while creating the bill.");
    } finally {
      setSaving(false);
    }
  };

  const handlePrintSave = async () => {
    if (printSaving || !validateBill()) return;
    try {
      setPrintSaving(true);
      const nextInvNo = await invoiceCountAPI();
      if (pdfModule === "WIFI") handleEposPrint(nextInvNo);
      else handleBillPdfDownload(nextInvNo);
      if (whatsAppCheck === true) {
        const pdf = await handlePdfDownload(nextInvNo);
        await sendWhatsAppAPI(pdf);
      }
      await submitBill();
    } catch (error) {
      console.error("Error creating bill:", error);
      toastError("Something went wrong while creating the bill.");
    } finally {
      setPrintSaving(false);
    }
  };

  /* -------------------------- print / share --------------------------- */

  const handleEposPrint = (nextInvNo) => {
    printReceipt(
      localIp,
      address,
      address1,
      city,
      gstNo,
      userPhone,
      nextInvNo,
      invNo,
      billDate,
      userDetails,
      customerName,
      userName,
      customerMobile,
      printItems,
      printTotals.gross,
      printTotals.dis,
      totalNetAmt,
      printTotals.qty,
      base64Images,
      base64QR,
      printTotals.groups,
      ins,
      ins1,
      ins2,
      ins3,
      ins4,
      ins5,
      ins6,
      gstValue,
      cashAmt,
      cardAmt,
      onlineAmt,
      upiAmt,
      creditAmt,
      { onError: toastError },
    );
  };

  const handleBillPdfDownload = (nextInvNo) => {
    BillPdfDownload(
      address,
      address1,
      city,
      gstNo,
      userPhone,
      nextInvNo,
      invNo,
      billDate,
      userDetails,
      customerName,
      userName,
      customerMobile,
      printItems,
      printTotals.gross,
      printTotals.dis,
      totalNetAmt,
      printTotals.qty,
      base64Images,
      base64QR,
      printTotals.groups,
      ins,
      ins1,
      ins2,
      ins3,
      ins4,
      ins5,
      ins6,
      gstValue,
      cashAmt,
      cardAmt,
      onlineAmt,
      upiAmt,
      creditAmt,
    );
  };

  // builds the invoice PDF in memory and returns it as raw base64 (used for WhatsApp)
  const handlePdfDownload = async (nextInvNo) => {
    const el = document.createElement("div");
    const clean = (v) => (v && v !== "-" ? v : "");
    const rows = printItems
      .map(
        (it, i) => `<tr>
          <td>${i + 1}</td>
          <td style="text-align:left">${it.ProdName}<br/><small>${it.DesignCode}${clean(it.HSNCODE) ? ` · HSN ${it.HSNCODE}` : ""}</small></td>
          <td>${it.stock}</td>
          <td>${Number(it.CPrice).toFixed(2)}</td>
          <td>${Number(it.Disamt).toFixed(2)}</td>
          <td style="text-align:right"><b>${(it.stock * it.offer).toFixed(2)}</b></td>
        </tr>`,
      )
      .join("");
    const gstRows =
      gstValue === true
        ? printTotals.groups
            .map(
              (r) =>
                `<tr><td>${r.Basic_amt.toFixed(2)}</td><td>${r.CGST_RATE}%</td><td>${r.CGST_AMT.toFixed(2)}</td><td>${r.SGST}%</td><td>${r.SGST_AMT.toFixed(2)}</td><td>${r.NET_AMT.toFixed(2)}</td></tr>`,
            )
            .join("")
        : "";
    const pay = (label, v) =>
      v > 0
        ? `<div style="display:flex;justify-content:space-between"><span>${label}</span><span>${Number(v).toFixed(2)}</span></div>`
        : "";

    el.innerHTML = `
      <style>
        table{width:100%;border-collapse:collapse;font-family:Arial,sans-serif;font-size:9px}
        th,td{border:1px solid #222;padding:3px;text-align:center}
        th{background:#162566;color:#fff;font-size:10px}
      </style>
      <div style="width:90mm;margin:0 auto;padding:0 5mm;box-sizing:border-box;font-family:Arial,sans-serif;font-size:10px">
        <div style="text-align:center;margin-bottom:5px">
          ${base64Images ? `<img src="${base64Images}" style="width:160px;height:160px;object-fit:contain"/>` : ""}
          <div style="font-weight:bold;font-size:12px">${address}</div>
          <div style="font-weight:bold;font-size:12px">${address1}</div>
          <div style="font-weight:bold;font-size:12px">${city}</div>
          ${isValidValue(gstNo) ? `<div style="font-weight:bold;font-size:12px">${gstNo}</div>` : ""}
          <div style="font-weight:bold;font-size:12px">PHONE NO: ${userPhone}</div>
        </div>
        <div style="border:1px solid #222;padding:6px;margin-bottom:5px;font-size:11px">
          <div style="background:#162566;color:#fff;text-align:center;font-weight:bold;padding:5px;margin:-6px -6px 6px">${gstValue === true ? "TAX INVOICE" : "BILL OF SUPPLY"}</div>
          <div>Invoice No : <b style="color:#d00">#${invNo ? invNo : nextInvNo}</b></div>
          <div>Date : <b>${dayjs(billDate || undefined).format("DD-MMM-YYYY hh:mm A")}</b></div>
          <div>Customer : <b>${userDetails?.Custname || customerName || "-"}</b></div>
          <div>Contact : <b>${userDetails?.Mobileno || customerMobile || "-"}</b></div>
        </div>
        <table>
          <thead><tr><th>#</th><th>PARTICULARS</th><th>QTY</th><th>RATE</th><th>DISC</th><th>AMOUNT</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div style="border:1px solid #222;margin-top:5px;font-size:11px">
          <div style="padding:6px">
            <div style="display:flex;justify-content:space-between"><span>Total Amount</span><span>${printTotals.gross.toFixed(2)}</span></div>
            <div style="display:flex;justify-content:space-between"><span>Discount</span><span>${printTotals.dis.toFixed(2)}</span></div>
          </div>
          <div style="background:#52bd91;color:#fff;font-weight:bold;display:flex;justify-content:space-between;padding:6px;font-size:13px"><span>Net Amount</span><span>${totalNetAmt.toFixed(2)}</span></div>
        </div>
        <div style="margin-top:5px;font-weight:bold">Total Qty : ${printTotals.qty}</div>
        ${gstRows ? `<table style="margin-top:8px"><thead><tr><th>BASIC</th><th>CGST %</th><th>CGST</th><th>SGST %</th><th>SGST</th><th>NET</th></tr></thead><tbody>${gstRows}</tbody></table>` : ""}
        <div style="border:1px solid #222;margin-top:6px;padding:6px;font-size:11px">
          ${pay("Cash Paid", cashAmt)}${pay("Card Paid", cardAmt)}${pay("UPI Paid", upiAmt)}${pay("Online Paid", onlineAmt)}${pay("Credit", creditAmt)}
        </div>
        <div style="margin-top:8px;font-size:9px">
          <div style="text-align:center;font-size:12px">Terms &amp; Conditions</div>
          <ul>${[ins, ins1, ins2, ins3, ins4, ins5, ins6].map((t) => `<li>${t ?? ""}</li>`).join("")}</ul>
        </div>
        <div style="background:#add8e6;text-align:center;padding:8px;border-radius:10px;font-size:12px">Thank you for shopping, please visit again!</div>
      </div>`;

    const opt = {
      margin: [0, 5, 0, 5],
      filename: `Invoice-${invNo || nextInvNo}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: [90, 297], orientation: "portrait" },
    };
    const dataUri = await html2pdf()
      .set(opt)
      .from(el)
      .outputPdf("datauristring");
    return dataUri?.replace(/^data:application\/pdf;[^,]*base64,/, "");
  };

  const sendWhatsAppAPI = async (fileBase) => {
    const payload = {
      inputJsons: [
        {
          id: "123",
          receiver: "919440353589",
          sender: "917995444031",
          key: "3MjswWu5Hk28BwFePsEdHxLWWY4KhqIRYYesuXmiv0AHI9PRatFW7O5s",
          campaignId: "971",
          templeteName: "salebill_loyalty_pdf",
          fileType: "DOCUMENT",
          fileName: "VEnu3.pdf",
          fileBase,
          clientName: "VENUGOPAL_WHATSAPP",
          params: "VENU,10,100,111,VENU JEWELLERY",
        },
      ],
    };
    try {
      await axios.post(
        `${CREATE_jwel}/api/WhatsApp/SendMessage`,
        payload,
        JSON_H,
      );
    } catch (error) {
      console.error("Error sending WhatsApp:", error);
    }
  };

  /* ------------------------ logo → base64 (for print) ------------------------ */

  const fetchWithRetry = async (url, retries = 3, delay = 300) => {
    for (let i = 0; i < retries; i++) {
      try {
        const res = await fetch(url, { mode: "cors" });
        if (res.ok) return res;
      } catch (e) {
        console.warn(`Retry ${i + 1} for ${url}`);
      }
      await new Promise((r) => setTimeout(r, delay));
    }
    throw new Error("Failed after retries: " + url);
  };

  const resizeBase64Img = (base64Str, maxWidth = 500, maxHeight = 500) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height *= maxWidth / width;
            width = maxWidth;
          } else {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = base64Str;
    });

  const urlToBase64 = async (url) => {
    const cleanUrl = decodeURIComponent(url);
    const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(cleanUrl)}`;
    const response = await fetchWithRetry(proxyUrl, 3, 800);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          resolve(await resizeBase64Img(reader.result));
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  useEffect(() => {
    const convertImage = async () => {
      try {
        setBase64Images(await urlToBase64(singleImage ?? ""));
      } catch (err) {
        console.error("Image conversion failed:", err);
      }
    };
    convertImage();
  }, [singleImage]);

  /* ------------------------------ scanner ----------------------------- */

  const SCAN_COOLDOWN_MS = 1000;

  const startScanner = async () => {
    try {
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        rememberLastUsedCamera: true,
      };
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;
      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        async (decodedText) => {
          if (scannedRef.current) return;
          scannedRef.current = true;
          try {
            await mainAPI(decodedText); // follows modeRef (SALE / EXCHANGE)
            await homeBillData(decodedText);
          } catch (err) {
            console.error("Scan handling error:", err);
          }
          setTimeout(() => {
            scannedRef.current = false;
          }, SCAN_COOLDOWN_MS);
        },
        (error) => console.warn("QR Scan Error:", error),
      );
    } catch (error) {
      console.error("Failed to start scanner:", error);
    }
  };

  const stopScanner = async () => {
    tagNoRef.current?.focus();
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      } finally {
        html5QrCodeRef.current = null;
        setQrOpen(false);
        scannedRef.current = false;
      }
    } else {
      setQrOpen(false);
      scannedRef.current = false;
    }
  };

  const handleOpenScanner = () => {
    setQrOpen(true);
    setTimeout(() => startScanner(), 300);
    tagNoRef.current?.focus();
  };

  /* ------------------------- small UI handlers ------------------------ */

  const handleOk = () => setCustomerOpen(true);
  const handleCancel = () => {
    setCustomerOpen(false);
    tagNoRef.current?.focus();
  };

  const handleCardOpen = (code) => {
    setCardOpen(true);
    const g = code.GSTApplied;
    const basic = code.stock * code.offer * (100 / (100 + g));
    setTitleValue(code.DesignCode);
    setGst(g);
    setGstAmt(basic * (g / 100));
    setCGstRate(g / 2);
    setCGstAmt((basic * (g / 100)) / 2);
    setSGstRate(g / 2);
    setSGstAmt((basic * (g / 100)) / 2);
    setIGstRate(g);
    setIGstAmt(0);
    setBasicAmt(basic);
    setNetAmt(code.stock * code.offer);
    setSmCode(code?.SMCODE);
  };
  const handleCardCancel = () => {
    setCardOpen(false);
    tagNoRef.current?.focus();
  };
  const handleModifyOpen = () => setModifyOpen(true);
  const handleModifyCancel = () => {
    setModifyOpen(false);
    tagNoRef.current?.focus();
  };
  const handleCashOpen = () => setCashOpen(true);
  const handleCashCancel = () => {
    setCashOpen(false);
    tagNoRef.current?.focus();
  };
  const handleQrcodeOpen = () => setQrCodeOpen(true);
  const handleQrcodeCancel = () => {
    setQrCodeOpen(false);
    tagNoRef.current?.focus();
  };
  const handleWhatsAppCancel = () => setWhatsAppOpen(false);

  const handleRest = () => {
    tagNoRef.current?.focus();
    invoiceCountAPI();
    commissionAPI();
    gstCheckAPI();
    onlineAccNoAPI();
    setBarCodeData([]);
    setExchangeData([]);
    setWasExchangeBill(false);
    setMode("SALE");
    modeRef.current = "SALE";
    dialogTargetRef.current = "SALE";
    setEditTarget("SALE");
    setScanResult("");
    setCustomerArea("");
    setCustomerName("");
    setCustomerMobile("");
    setInvNo(0);
    setModifyCode();
    setCashValue("CASH");
    setCashAmt(0);
    setCardAmt(0);
    setUpiAmt(0);
    setOnlineAmt(0);
    setCreditAmt(0);
    setCreditCardAmt(0);
    setCreditUpiAmt(0);
    setCreditOnlineAmt(0);
    setCreditCashAmt(0);
    setCreditCashValue("CREDIT");
    setSwitchCredit("Cash");
    setBillDate();
    setQtyData([]);
    setOfferAData([]);
    setOfferARate(0);
    setOfferBData([]);
    setOfferBRate(0);
    setUserDetails({});
    setPhoneNo();
    setFormValues(EMPTY_CUSTOMER_FORM);
  };

  const handleTagNoKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitRef.current?.click();
    }
  };

  const handleMobileNoKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchRef.current?.click();
    }
  };

  const handleSubmitCode = async () => {
    if (!scanResult) {
      message.warning(isExchange ? "Enter Exchange Code" : "Enter Design Code");
      return;
    }
    const code = scanResult;
    await mainAPI(code);
    await homeBillData(code);
  };

  // props for the edit-qty dialog – it edits whichever list the tapped card belongs to
  const editIsExchange = editTarget === "EXCHANGE";

  /* ------------------------------- view ------------------------------- */

  return (
    <div>
      {contextHolder}
      <Header setOpen={setOpen} />
      <SidebarDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        singleImage={singleImage}
        userArea={userArea}
        userName={userName}
      />

      <div className="bcs" data-mode={mode}>
        <style>{`
          @import url("https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap");

          .bcs {
            --navy: #121c4d;
            --navy-2: #1d2f7a;
            --ink: #151a36;
            --muted: #7b84a0;
            --paper: #edf2fa;
            --card: #ffffff;
            --line: #e0e7f4;
            --tint: #f3f6fc;
            --mint: #43bd98;
            --mint-deep: #229a79;
            --mint-soft: #e4f6ef;
            --gold: #e0b04f;
            --gold-deep: #c8912b;
            --rose: #e0475f;
            --rose-soft: #fdeef1;
            /* accent follows the active mode (SALE = mint, EXCHANGE = violet) */
            --acc: var(--mint);
            --acc-deep: var(--mint-deep);
            --acc-soft: var(--mint-soft);
            --acc-ink: #0d5c43;
            background: var(--paper);
            padding: 8px 10px 190px;
            color: var(--ink);
            font-family: "Manrope", "Segoe UI", system-ui, -apple-system, Roboto, Arial, sans-serif;
            font-size: 11.5px;
            min-height: 100%;
          }
          .bcs[data-mode="EXCHANGE"] {
            --acc: #8b5cf6;
            --acc-deep: #6d28d9;
            --acc-soft: #efe9fe;
            --acc-ink: #4c1d95;
          }
          .bcs *, .bcs *::before, .bcs *::after { box-sizing: border-box; }
          .bcs .ant-btn, .bcs .ant-input { font-family: inherit; }
          .bcs button { font-family: inherit; }

          /* ---- layout ---- */
          .bcs-layout { display: block; }

          /* ---- hero ---- */
          .bcs-hero {
            position: relative; overflow: hidden;
            display: flex; align-items: center; justify-content: space-between; gap: 8px;
            padding: 8px 10px; border-radius: 14px; color: #fff;
            background: linear-gradient(135deg, var(--navy) 0%, var(--navy-2) 100%);
            box-shadow: 0 8px 18px -12px rgba(18, 28, 77, 0.8);
          }
          .bcs-hero::after {
            content: ""; position: absolute; right: -26px; top: -34px; width: 100px; height: 100px;
            border-radius: 50%; background: radial-gradient(circle, var(--acc), transparent 70%); opacity: .45;
            pointer-events: none; transition: background .25s ease;
          }
          .bcs-hero-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; position: relative; z-index: 1; }
          .bcs-hero-label { font-size: 9.5px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; opacity: 0.7; }
          .bcs-hero-row { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
          .bcs-hero-no { font-size: 19px; font-weight: 800; line-height: 1.1; font-variant-numeric: tabular-nums; }
          .bcs-hero-date { font-size: 9.5px; font-weight: 600; padding: 2px 7px; border-radius: 999px; background: rgba(255,255,255,0.14); }
          .bcs-hero-cust {
            display: inline-flex; align-items: center; gap: 4px; max-width: 190px; margin-top: 2px;
            font-size: 10px; font-weight: 600; color: #aef0d9;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          }
          .bcs-hero-actions { display: flex; gap: 6px; position: relative; z-index: 1; }
          .bcs-ico {
            width: 30px; height: 30px; display: grid; place-items: center; padding: 0;
            border-radius: 10px; color: #fff; cursor: pointer;
            background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.18);
            transition: transform .15s ease, background .15s ease;
          }
          .bcs-ico:active { transform: scale(0.92); background: rgba(255,255,255,0.22); }
          .bcs-ico.mint { color: #8df0cf; }
          .bcs-ico.gold { color: #ffdf8a; }

          /* ---- SALE / EXCHANGE switch ---- */
          .bcs-switch {
            position: relative; display: grid; grid-template-columns: 1fr 1fr; margin-top: 8px; padding: 3px;
            border-radius: 14px; background: #dfe6f5; border: 1px solid var(--line);
          }
          .bcs-switch::before {
            content: ""; position: absolute; top: 3px; bottom: 3px; left: 3px; width: calc(50% - 3px);
            border-radius: 11px; background: linear-gradient(135deg, var(--acc), var(--acc-deep));
            box-shadow: 0 6px 12px -8px var(--acc-deep); transition: transform .25s ease;
          }
          .bcs[data-mode="EXCHANGE"] .bcs-switch::before { transform: translateX(100%); }
          .bcs-sw-btn {
            position: relative; z-index: 1; height: 36px; border: 0; background: transparent; cursor: pointer;
            display: flex; align-items: center; justify-content: center; gap: 6px;
            font-size: 12px; font-weight: 800; letter-spacing: 0.6px; color: var(--muted);
            transition: color .2s ease; -webkit-tap-highlight-color: transparent;
          }
          .bcs-sw-btn.on { color: #fff; }
          .bcs-sw-count {
            min-width: 18px; height: 18px; padding: 0 5px; display: grid; place-items: center;
            font-size: 10px; font-weight: 800; border-radius: 999px; background: rgba(18,28,77,0.12);
          }
          .bcs-sw-btn.on .bcs-sw-count { background: rgba(255,255,255,0.28); }

          /* ---- entry ---- */
          .bcs-entry {
            margin-top: 8px; padding: 9px; border-radius: 14px;
            background: var(--card); border: 1px solid var(--line);
            box-shadow: 0 6px 16px -14px rgba(18, 28, 77, 0.5);
          }
          .bcs-entry-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px; min-height: 16px; }
          .bcs-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; color: var(--acc-deep); }
          .bcs-wa { color: #1fb255; display: inline-flex; }
          .bcs .bcs-input.ant-input {
            height: 34px; border-radius: 10px; font-size: 13px; font-weight: 700; letter-spacing: 1px;
            color: var(--ink); background: var(--tint); border: 1px solid var(--line); padding: 0 11px;
          }
          .bcs .bcs-input.ant-input:hover,
          .bcs .bcs-input.ant-input:focus {
            background: #fff; border-color: var(--acc-deep); box-shadow: 0 0 0 3px rgba(29,47,122,0.1);
          }
          .bcs-entry-actions { display: grid; grid-template-columns: 1fr 78px; gap: 7px; margin-top: 7px; }
          .bcs .bcs-btn-submit.ant-btn {
            height: 32px; border: 0; border-radius: 10px; font-weight: 700; font-size: 12px; color: #fff;
            background: linear-gradient(135deg, var(--navy), var(--navy-2));
            box-shadow: 0 6px 12px -9px rgba(18,28,77,0.9);
          }
          .bcs[data-mode="EXCHANGE"] .bcs-btn-submit.ant-btn { background: linear-gradient(135deg, var(--acc), var(--acc-deep)); }
          .bcs .bcs-btn-reset.ant-btn {
            height: 32px; border-radius: 10px; font-weight: 700; font-size: 12px;
            color: var(--rose); background: var(--rose-soft); border: 1px solid #f8d2d9; box-shadow: none;
          }

          /* ---- list ---- */
          .bcs-list-head { display: flex; align-items: center; justify-content: space-between; margin: 11px 3px 6px; }
          .bcs-list-title { font-size: 11.5px; font-weight: 800; letter-spacing: 0.3px; color: var(--navy); }
          .bcs-count {
            min-width: 20px; height: 20px; padding: 0 6px; display: grid; place-items: center;
            font-size: 10.5px; font-weight: 800; color: #fff; border-radius: 999px;
            background: linear-gradient(135deg, var(--acc), var(--acc-deep));
          }
          .bcs-empty { padding: 12px; text-align: center; border-radius: 14px; background: var(--card); border: 1px dashed #cdd7ee; }
          .bcs-empty img { width: 62px; opacity: 0.85; }
          .bcs-empty p { margin: 4px 0 0; font-size: 11px; color: var(--muted); font-weight: 600; }
          .bcs-list { display: block; }

          .bcs-item {
            position: relative; margin-bottom: 8px; padding: 8px 9px 8px 12px; border-radius: 14px;
            background: var(--card); border: 1px solid var(--line); overflow: hidden;
            box-shadow: 0 6px 14px -12px rgba(18,28,77,0.5); scroll-margin-bottom: 190px;
          }
          .bcs-item::before {
            content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3.5px;
            background: linear-gradient(180deg, var(--acc), var(--acc-deep));
          }
          .bcs-item-top { display: flex; align-items: center; gap: 6px; justify-content: space-between; }
          .bcs-idx {
            min-width: 20px; height: 20px; padding: 0 6px; display: grid; place-items: center;
            font-size: 9.5px; font-weight: 800; color: #fff; border-radius: 999px;
            background: linear-gradient(135deg, var(--navy), var(--navy-2));
          }
          .bcs-code {
            min-width: 0; padding: 3px 8px; font-size: 11.5px; font-weight: 800; letter-spacing: 0.3px;
            color: var(--acc-ink); background: var(--acc-soft); border-radius: 8px;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          }
          .bcs-mini {
            width: 26px; height: 26px; display: grid; place-items: center; padding: 0; cursor: pointer;
            border-radius: 8px; border: 0;
          }
          .bcs-mini.tax { color: var(--navy-2); background: #e9edfb; }
          .bcs-mini.del { color: var(--rose); background: var(--rose-soft); }
          .bcs-name { margin: 5px 1px 6px; font-size: 10.5px; font-weight: 600; color: var(--muted); line-height: 1.3; }
          .bcs-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; }
          .bcs-cell { padding: 3px 6px; background: var(--tint); border-radius: 8px; min-width: 0; }
          .bcs-cell span { display: block; font-size: 8.5px; font-weight: 600; letter-spacing: 0.3px; text-transform: uppercase; color: var(--muted); }
          .bcs-cell b { display: block; font-size: 11px; font-weight: 800; color: var(--ink); font-variant-numeric: tabular-nums; overflow: hidden; text-overflow: ellipsis; }
          .bcs-item-foot { display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-top: 7px; }
          .bcs-qty {
            display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; font-size: 10px; font-weight: 600;
            color: var(--muted); background: var(--tint); border: 1px solid var(--line); border-radius: 999px;
          }
          .bcs-qty strong { font-size: 12px; font-weight: 800; color: var(--ink); }
          .bcs-qty.editable { color: var(--navy-2); cursor: pointer; }
          .bcs-amt {
            display: flex; align-items: baseline; gap: 5px; padding: 3px 11px; border-radius: 999px; color: #fff;
            background: linear-gradient(135deg, var(--acc), var(--acc-deep));
          }
          .bcs-amt span { font-size: 9px; font-weight: 600; opacity: 0.85; }
          .bcs-amt strong { font-size: 13px; font-weight: 800; font-variant-numeric: tabular-nums; }

          /* ---- bottom bar ---- */
          .bcs-bar {
            position: fixed; left: 50%; bottom: 0; transform: translateX(-50%); width: 100%; max-width: 560px;
            z-index: 20; padding: 9px 12px calc(9px + env(safe-area-inset-bottom, 0px));
            background: rgba(255,255,255,0.96); backdrop-filter: blur(10px);
            border-radius: 18px 18px 0 0; border-top: 1px solid var(--line);
            box-shadow: 0 -10px 24px -14px rgba(18,28,77,0.5);
          }
          .bcs-bar-top { display: flex; align-items: flex-end; justify-content: space-between; gap: 8px; }
          .bcs-net-label { font-size: 9.5px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: var(--muted); }
          .bcs-net { font-size: 21px; font-weight: 800; line-height: 1.1; color: var(--navy); font-variant-numeric: tabular-nums; }
          .bcs-net.neg { color: var(--rose); }
          .bcs-qtybadge {
            padding: 3px 12px; font-size: 11px; font-weight: 700; color: #0d5c43; white-space: nowrap;
            background: var(--mint-soft); border: 1px solid #b8e6d6; border-radius: 999px;
          }
          .bcs-qtybadge b { font-size: 12.5px; font-weight: 800; margin-left: 2px; }
          .bcs-qtybadge.ex { color: #4c1d95; background: #efe9fe; border-color: #d9ccfb; margin-left: 4px; }
          .bcs-badges { display: flex; align-items: center; }
          .bcs-chips { display: flex; gap: 6px; margin-top: 7px; }
          .bcs-chip { flex: 1; min-width: 0; padding: 3px 10px; font-size: 9px; font-weight: 700; letter-spacing: 0.3px; text-transform: uppercase; color: var(--muted); background: var(--tint); border-radius: 9px; }
          .bcs-chip b { display: block; font-size: 11.5px; font-weight: 800; color: var(--ink); text-transform: none; letter-spacing: 0; font-variant-numeric: tabular-nums; }
          .bcs-chip.ex b { color: #6d28d9; }
          .bcs-bar-actions { display: flex; align-items: center; gap: 7px; margin-top: 8px; }
          .bcs .bcs-save.ant-btn, .bcs .bcs-print.ant-btn {
            flex: 1; height: 36px; border: 0; border-radius: 12px; font-size: 12px; font-weight: 800;
            letter-spacing: 0.5px; color: #fff; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
          }
          .bcs .bcs-save.ant-btn { background: linear-gradient(135deg, var(--mint), var(--mint-deep)); box-shadow: 0 7px 14px -10px rgba(34,154,121,0.95); }
          .bcs .bcs-print.ant-btn { background: linear-gradient(135deg, var(--gold), var(--gold-deep)); box-shadow: 0 7px 14px -10px rgba(200,145,43,0.95); }
          .bcs .bcs-save.ant-btn:disabled, .bcs .bcs-print.ant-btn:disabled { opacity: 0.7; color: #fff; }
          .bcs-sq {
            width: 36px; height: 36px; flex-shrink: 0; display: grid; place-items: center; padding: 0; cursor: pointer;
            border-radius: 12px; color: var(--navy); background: var(--tint); border: 1px solid var(--line);
            transition: transform .15s ease;
          }
          .bcs-sq:active { transform: scale(0.92); }
          .bcs-sq.qr { color: #fff; border: 0; background: linear-gradient(135deg, var(--navy), var(--navy-2)); }

          @media (max-width: 340px) { .bcs-grid { grid-template-columns: repeat(2, 1fr); } }

          /* ---- tablet: 2-column layout, sticky entry panel, wide bottom bar ---- */
          @media (min-width: 700px) {
            .bcs { max-width: 1100px; margin: 0 auto; padding: 12px 14px 150px; font-size: 12.5px; }
            .bcs-layout { display: grid; grid-template-columns: minmax(300px, 340px) 1fr; gap: 14px; align-items: start; }
            .bcs-side { position: sticky; top: 8px; }
            .bcs-main .bcs-list-head { margin-top: 0; }
            .bcs-hero { padding: 12px 14px; }
            .bcs-hero-no { font-size: 24px; }
            .bcs-ico { width: 38px; height: 38px; }
            .bcs-sw-btn { height: 42px; font-size: 13px; }
            .bcs .bcs-input.ant-input { height: 42px; font-size: 15px; }
            .bcs-entry-actions { grid-template-columns: 1fr 96px; }
            .bcs .bcs-btn-submit.ant-btn, .bcs .bcs-btn-reset.ant-btn { height: 40px; font-size: 13px; }
            .bcs-code { font-size: 13px; }
            .bcs-name { font-size: 11.5px; }
            .bcs-cell b { font-size: 12.5px; }
            .bcs-cell span { font-size: 9.5px; }
            .bcs-mini { width: 32px; height: 32px; }
            .bcs-amt strong { font-size: 15px; }
            .bcs-item { scroll-margin-bottom: 150px; }

            .bcs-bar {
              max-width: 1100px; display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 18px;
              padding: 10px 18px calc(10px + env(safe-area-inset-bottom, 0px));
            }
            .bcs-bar-top { flex-direction: column; align-items: flex-start; gap: 4px; }
            .bcs-net { font-size: 26px; }
            .bcs-chips { margin-top: 0; }
            .bcs-chip b { font-size: 13px; }
            .bcs-bar-actions { margin-top: 0; }
            .bcs .bcs-save.ant-btn, .bcs .bcs-print.ant-btn { flex: none; width: 130px; height: 42px; font-size: 13px; }
            .bcs-sq { width: 42px; height: 42px; }
          }
          /* wide tablets / landscape: item cards in two columns */
          @media (min-width: 900px) {
            .bcs-list { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
            .bcs-list .bcs-item { margin-bottom: 0; }
          }
        `}</style>

        <div ref={qrRef} style={{ display: "none" }}>
          <QRCodeCanvas value={upiLink} size={200} includeMargin={true} />
        </div>

        <div className="bcs-layout">
          {/* ================= LEFT / TOP : hero + switch + entry ================= */}
          <div className="bcs-side">
            {/* ---------- Invoice hero ---------- */}
            <div className="bcs-hero">
              <div className="bcs-hero-main">
                <span className="bcs-hero-label">
                  {invNo
                    ? "Editing invoice"
                    : exchangeData.length
                      ? "New exchange invoice"
                      : "New invoice"}
                </span>
                <div className="bcs-hero-row">
                  <span className="bcs-hero-no">
                    #{invNo ? invNo : invoiceNo}
                  </span>
                  <span className="bcs-hero-date">
                    {dayjs(billDate || undefined).format("DD-MMM-YYYY")}
                  </span>
                </div>
                {heroCustomer ? (
                  <span className="bcs-hero-cust">
                    <Ico name="user" size={11} />
                    {heroCustomer}
                  </span>
                ) : null}
              </div>
              <div className="bcs-hero-actions">
                <button
                  className="bcs-ico mint"
                  onClick={handleModifyOpen}
                  aria-label="Modify invoice"
                >
                  <Ico name="edit" size={15} />
                </button>
                <button
                  className="bcs-ico gold"
                  onClick={() => setCustOpen(true)}
                  aria-label="Customer"
                >
                  <Ico name="userPlus" size={15} />
                </button>
              </div>
            </div>

            {/* ---------- SALE / EXCHANGE switch ---------- */}
            <div className="bcs-switch" role="tablist" aria-label="Bill mode">
              <button
                role="tab"
                aria-selected={!isExchange}
                className={`bcs-sw-btn${!isExchange ? " on" : ""}`}
                onClick={() => changeMode("SALE")}
              >
                <Ico name="cart" size={15} />
                SALE
                <span className="bcs-sw-count">{barCodeData.length}</span>
              </button>
              <button
                role="tab"
                aria-selected={isExchange}
                className={`bcs-sw-btn${isExchange ? " on" : ""}`}
                onClick={() => changeMode("EXCHANGE")}
              >
                <Ico name="swap" size={15} />
                EXCHANGE
                <span className="bcs-sw-count">{exchangeData.length}</span>
              </button>
            </div>

            {/* ---------- Design code entry ---------- */}
            <div className="bcs-entry">
              <div className="bcs-entry-head">
                <span className="bcs-label">
                  {isExchange ? "Exchange Code" : "Design Code"}
                </span>
                {whatsAppCheck === true ? (
                  <span className="bcs-wa">
                    <Ico name="chat" size={16} />
                  </span>
                ) : null}
              </div>
              <Input
                ref={tagNoRef}
                autoFocus={true}
                className="bcs-input"
                value={scanResult}
                onKeyDown={handleTagNoKeyDown}
                onChange={(e) =>
                  setScanResult(
                    e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                  )
                }
              />
              <div className="bcs-entry-actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  ref={submitRef}
                  className="bcs-btn-submit"
                  onClick={handleSubmitCode}
                >
                  {isExchange ? "Add Exchange" : "Submit"}
                </Button>
                <Button
                  type="primary"
                  danger
                  className="bcs-btn-reset"
                  onClick={handleRest}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* ================= RIGHT / BOTTOM : product list ================= */}
          <div className="bcs-main">
            <div className="bcs-list-head">
              <span className="bcs-list-title">
                {isExchange ? "Exchange Items" : "Product Details"}
              </span>
              {activeItems.length > 0 && (
                <span className="bcs-count">{activeItems.length}</span>
              )}
            </div>

            {activeItems.length === 0 ? (
              <div className="bcs-empty">
                <img src={noData} alt="No Data" />
                <p>
                  {isExchange ? "No Exchange Items Added" : "No Data Found"}
                </p>
              </div>
            ) : (
              <div className="bcs-list">
                {activeItems.map((code, index) => (
                  <div
                    key={`${mode}-${code.DesignCode}-${index}`}
                    ref={index === activeItems.length - 1 ? lastItemRef : null}
                    className="bcs-item"
                  >
                    <div className="bcs-item-top">
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                          minWidth: 0,
                        }}
                      >
                        <span className="bcs-idx">#{index + 1}</span>
                        <span className="bcs-code">{code.DesignCode}</span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                        }}
                      >
                        <button
                          className="bcs-mini tax"
                          onClick={() => handleCardOpen(code)}
                          aria-label="Tax details"
                        >
                          <Ico name="receipt" size={13} />
                        </button>
                        {canEdit && (
                          <button
                            className="bcs-mini del"
                            onClick={() => handleDelete(index, mode)}
                            aria-label="Delete item"
                          >
                            <Ico name="trash" size={13} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="bcs-name">{code.ProdName}</div>

                    <div className="bcs-grid">
                      <div className="bcs-cell">
                        <span>MRP</span>
                        <b>{Number(code.CPrice).toFixed(2)}</b>
                      </div>
                      <div className="bcs-cell">
                        <span>Disc</span>
                        <b>{Number(code.Disper) + "%"}</b>
                      </div>
                      <div className="bcs-cell">
                        <span>Dis.Amt</span>
                        <b>{Number(code.Disamt).toFixed(2)}</b>
                      </div>
                      <div className="bcs-cell">
                        <span>Rate</span>
                        <b>{Number(code.offer).toFixed(2)}</b>
                      </div>
                    </div>

                    <div className="bcs-item-foot">
                      <div
                        className={`bcs-qty${canEdit ? " editable" : ""}`}
                        onClick={() => {
                          if (canEdit) {
                            setEditTarget(mode);
                            setItemDetailsOpen(true);
                            setItemDetailsData(code);
                            setItemDEsignCode(index);
                          }
                        }}
                      >
                        QTY <strong>{code.stock}</strong>
                        {canEdit && <Ico name="edit" size={11} />}
                      </div>
                      <div className="bcs-amt">
                        <span>{isExchange ? "Exch" : "Amt"}</span>
                        <strong>
                          {Number(code.stock * code.offer).toFixed(2)}
                        </strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ---------- Bottom checkout bar ---------- */}
        <div className="bcs-bar">
          <div className="bcs-bar-top">
            <div>
              <div className="bcs-net-label">
                {totalNetAmt < 0 ? "Refund To Customer" : "Net Amount"}
              </div>
              <div className={`bcs-net${totalNetAmt < 0 ? " neg" : ""}`}>
                {"₹ " + Number(totalNetAmt).toFixed(2)}
              </div>
            </div>
            <div className="bcs-badges">
              <span className="bcs-qtybadge">
                QTY <b>{saleT.qty}</b>
              </span>
              {exchangeData.length > 0 && (
                <span className="bcs-qtybadge ex">
                  EXCH <b>{exT.qty}</b>
                </span>
              )}
            </div>
          </div>

          <div className="bcs-chips">
            {exchangeData.length > 0 ? (
              <>
                <div className="bcs-chip">
                  Bill
                  <b>{"₹ " + Number(saleT.net).toFixed(2)}</b>
                </div>
                <div className="bcs-chip ex">
                  Exchange
                  <b>{"- ₹ " + Number(exT.net).toFixed(2)}</b>
                </div>
              </>
            ) : (
              <>
                <div className="bcs-chip">
                  Gross
                  <b>{"₹ " + Number(totalAmount).toFixed(2)}</b>
                </div>
                <div className="bcs-chip">
                  Discount
                  <b>{"₹ " + Number(totalDisAmt).toFixed(2)}</b>
                </div>
              </>
            )}
          </div>

          <div className="bcs-bar-actions">
            <Button
              disabled={saving}
              loading={saving}
              className="bcs-save"
              icon={<Ico name="save" size={15} />}
              onClick={handleSave}
            >
              {invNo ? "UPDATE" : "SAVE"}
            </Button>
            <Button
              disabled={printSaving}
              loading={printSaving}
              className="bcs-print"
              icon={<Ico name="printer" size={15} />}
              onClick={handlePrintSave}
            >
              PRINT
            </Button>
            {hasItems && (
              <button
                className="bcs-sq"
                onClick={handleCashOpen}
                aria-label="Payment mode"
              >
                <Ico name="wallet" size={18} />
              </button>
            )}
            {hasItems && upiAmt > 0 && (
              <button
                className="bcs-sq qr"
                onClick={handleQrcodeOpen}
                aria-label="UPI QR code"
              >
                <Ico name="qr" size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Scanner ---------- */}
      <>
        {scanOpen === true && !qrOpen && (
          <div className={styles.scanIconContainer} onClick={handleOpenScanner}>
            <span style={{ color: "white", display: "grid" }}>
              <Ico name="scan" size={28} />
            </span>
          </div>
        )}
        {qrOpen && (
          <div className={styles.qrScannerOverlay}>
            <div className={styles.qrScannerContent}>
              <div className={styles.closeIcon} onClick={stopScanner}>
                <span style={{ color: "#fff", display: "grid" }}>
                  <Ico name="close" size={28} />
                </span>
              </div>
              <div
                id="qr-reader"
                style={{ width: "300px", height: "300px" }}
              ></div>
            </div>
          </div>
        )}
      </>

      {/* ---------- Dialogs ---------- */}
      <CustomerInformation
        customerOpen={customerOpen}
        setCustomerOpen={setCustomerOpen}
        handleOk={handleOk}
        handleCancel={handleCancel}
        customerArea={customerArea}
        setCustomerArea={setCustomerArea}
        customerName={customerName}
        setCustomerName={setCustomerName}
        customerMobile={customerMobile}
        setCustomerMobile={setCustomerMobile}
      />
      <CustomerDialog
        open={custOpen}
        onCancel={() => {
          setCustOpen(false);
          tagNoRef.current?.focus();
        }}
        onSave={createCustomerDetailsAPI}
        formValues={formValues}
        setFormValues={setFormValues}
        deleteCustomerDetailsAPI={deleteCustomerDetailsAPI}
        phoneNo={phoneNo}
        setPhoneNo={setPhoneNo}
        userDetailsAPI={userDetailsAPI}
        phone1Ref={mobileRef}
        searchRef={searchRef}
        handleMobileNoKeyDown={handleMobileNoKeyDown}
      />
      <SingleCardDetails
        cardOpen={cardOpen}
        setCardOpen={setCardOpen}
        handleOk={handleCardCancel}
        handleCancel={handleCardCancel}
        titleValue={titleValue}
        gst={gst}
        gstAmt={gstAmt}
        cGstRate={cGstRate}
        cGstAmt={cGstAmt}
        sGstRate={sGstRate}
        sGstAmt={sGstAmt}
        iGstRate={iGstRate}
        iGstAmt={iGstAmt}
        basicAmt={basicAmt}
        netAmt={netAmt}
        smCode={smCode}
      />
      <ModifyInvoiceNo
        modifyOpen={modifyOpen}
        setModifyOpen={setModifyOpen}
        handleOk={handleModifyOpen}
        handleCancel={handleModifyCancel}
        modifyCode={modifyCode}
        setModifyCode={setModifyCode}
        modifyDetAPI={modifyDetAPI}
        modifyMastAPI={modifyMastAPI}
        modifyOnlineAPI={modifyOnlineAPI}
        modifyOpeningLedgerAPI={modifyOpeningLedgerAPI}
        handleRest={handleRest}
        tagNoRef={tagNoRef}
      />
      <CashMethod
        cashOpen={cashOpen}
        setCashOpen={setCashOpen}
        handleOk={handleCashOpen}
        handleCancel={handleCashCancel}
        cashValue={cashValue}
        setCashValue={setCashValue}
        totalNetAmt={totalNetAmt}
        cashAmt={cashAmt}
        setCashAmt={setCashAmt}
        cardAmt={cardAmt}
        setCardAmt={setCardAmt}
        upiAmt={upiAmt}
        setUpiAmt={setUpiAmt}
        onlineAmt={onlineAmt}
        setOnlineAmt={setOnlineAmt}
        creditAmt={creditAmt}
        setCreditAmt={setCreditAmt}
        setSwitchCredit={setSwitchCredit}
        switchCredit={switchCredit}
        creditCashValue={creditCashValue}
        setCreditCashValue={setCreditCashValue}
        creditCashAmt={creditCashAmt}
        setCreditCashAmt={setCreditCashAmt}
        creditCardAmt={creditCardAmt}
        setCreditCardAmt={setCreditCardAmt}
        creditUpiAmt={creditUpiAmt}
        setCreditUpiAmt={setCreditUpiAmt}
        creditOnlineAmt={creditOnlineAmt}
        setCreditOnlineAmt={setCreditOnlineAmt}
      />
      <WhatsAppDialog
        whatsAppOpen={whatsAppOpen}
        handleWhatsAppCancel={handleWhatsAppCancel}
        whatsAppCheck={whatsAppCheck}
        setWhatsAppCheck={setWhatsAppCheck}
      />
      <HomeBillDialog
        homeDetailsOpen={homeDetailsOpen}
        setHomeDetailsOpen={setHomeDetailsOpen}
        qty={qty}
        setQty={setQty}
        amount={amount}
        setAmount={setAmount}
        disCount={disCount}
        setDisCount={setDisCount}
        rate={rate}
        setRate={setRate}
        disAmt={disAmt}
        setDisAmt={setDisAmt}
        productName={productName}
        setProductName={setProductName}
        designCode={designCode}
        setDesignCode={setDesignCode}
        setPTypeName={setPTypeName}
        setHsnCode={setHsnCode}
        productData={productData}
        setProductData={setProductData}
        setBrandName={setBrandName}
        handleHomeBillSave={handleHomeBillSave}
      />
      <TaansDetailsDialog
        taansOpen={taansOpen}
        setTaansOpen={setTaansOpen}
        taansData={taansData}
        setTaansData={setTaansData}
        handleTaansBillSave={handleTaansBillSave}
      />
      <OfferDetailsADialog
        taansOpen={offerAOpen}
        setTaansOpen={setOfferAOpen}
        taansData={offerAData}
        setTaansData={setOfferAData}
        handleTaansBillSave={handleOfferABillSave}
        offerARate={offerARate}
      />
      <OfferDetailsBDialog
        taansOpen={offerBOpen}
        setTaansOpen={setOfferBOpen}
        taansData={offerBData}
        setTaansData={setOfferBData}
        handleTaansBillSave={handleOfferBBillSave}
        offerARate={offerBRate}
      />
      <EditItemDetails
        itemDetailsOpen={itemDetailsOpen}
        setItemDetailsOpen={setItemDetailsOpen}
        setItemDetailsData={setItemDetailsData}
        itemDetailsData={itemDetailsData}
        itemDesignCode={itemDesignCode}
        setBarCodeData={editIsExchange ? setExchangeData : setBarCodeData}
        barCodeData={editIsExchange ? exchangeData : barCodeData}
        qtyData={editIsExchange ? exchangeQtyData : qtyData}
        offerARate={offerARate}
        offerBRate={offerBRate}
        adminType={adminType}
      />
      <QrCodeDialog
        qrCodeOpen={qrCodeOpen}
        handleQrcodeCancel={handleQrcodeCancel}
        base64QR={base64QR}
      />
    </div>
  );
};

export default ExchangeBill;
